import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import Layout from '../components/Layout.js';
import Modal from '../components/Modal.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { readError } from './LinkPages.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LearnedTerm {
  id: string;
  term: string;
  field: 'jobTitle' | 'company';
  totalCount: number;
  orgCount: number;
  active: boolean;
  updatedAt: string;
}

interface OcrStat {
  key: string;
  value: number;
  samples: number;
  updatedAt: string;
}

const STAT_LABELS: Record<string, { de: string; en: string }> = {
  name_font_height_ratio: { de: 'Schriftgrößen-Verhältnis Name/Fließtext', en: 'Name-to-body font height ratio' },
  name_index_pct: { de: 'Relative Position der Namenszeile (0 = oben)', en: 'Relative name line position (0 = top)' },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function OcrLearning() {
  const { t, i18n } = useAppTranslation();
  const { user, loading } = useAuth();

  const [terms, setTerms] = useState<LearnedTerm[] | null>(null);
  const [stats, setStats] = useState<OcrStat[]>([]);
  const [minOrgs, setMinOrgs] = useState(3);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<LearnedTerm | null>(null);

  const load = useCallback(async () => {
    const [termsRes, statsRes] = await Promise.all([
      fetch('/api/admin/ocr-learning/terms', { credentials: 'include' }),
      fetch('/api/admin/ocr-learning/stats', { credentials: 'include' }),
    ]);
    if (!termsRes.ok) { setError(await readError(termsRes)); return; }
    const termsData = (await termsRes.json()) as { minOrgs: number; terms: LearnedTerm[] };
    setMinOrgs(termsData.minOrgs);
    setTerms(termsData.terms);
    if (statsRes.ok) setStats((await statsRes.json()) as OcrStat[]);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function removeTerm(term: LearnedTerm) {
    const res = await fetch(`/api/admin/ocr-learning/terms/${term.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok && res.status !== 204) { setError(await readError(res)); return; }
    void load();
  }

  if (!loading && user && user.role !== 'superadmin') return <Navigate to="/" replace />;

  const lang = (i18n.language ?? 'de').startsWith('en') ? 'en' : 'de';

  return (
    <Layout title={t('ocrLearning.title', 'OCR-Lernbasis')}>
      <div className="max-w-5xl space-y-5">
        <p className="text-sm text-ink-muted">
          {t('ocrLearning.intro', 'Vokabular, das die Visitenkarten-Erkennung aus manuellen Korrekturen gelernt hat. Ein Begriff wird erst ab Bestätigungen aus {{min}} Organisationen aktiv. Es werden nur generische Wörter gespeichert — keine Namen, Zeilen oder Karteninhalte.', { min: minOrgs })}
        </p>

        {/* Anonymous layout aggregates */}
        {stats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map((s) => (
              <div key={s.key} className="rounded-xl bg-panel border border-line p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  {STAT_LABELS[s.key]?.[lang] ?? s.key}
                </p>
                <p className="mt-1 text-2xl font-semibold text-ink">
                  {s.key === 'name_index_pct' ? `${(s.value * 100).toFixed(1)} %` : s.value.toFixed(2)}
                </p>
                <p className="text-xs text-ink-faint">
                  {t('ocrLearning.samples', '{{count}} Samples', { count: s.samples })}
                </p>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-600 dark:text-danger-300">{error}</div>
        )}

        {/* Terms table */}
        {terms === null && !error && <div className="h-40 rounded-xl bg-panel border border-line animate-pulse" />}
        {terms && terms.length === 0 && (
          <div className="rounded-xl bg-panel border border-line p-12 text-center text-ink-faint">
            {t('ocrLearning.empty', 'Noch keine gelernten Begriffe. Sie entstehen, wenn Nutzer gescannte Zeilen manuell zuordnen.')}
          </div>
        )}

        {terms && terms.length > 0 && (
          <div className="rounded-xl bg-panel border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  <th className="px-4 py-3">{t('ocrLearning.colTerm', 'Begriff')}</th>
                  <th className="px-4 py-3">{t('ocrLearning.colField', 'Feld')}</th>
                  <th className="px-4 py-3">{t('ocrLearning.colConfirmations', 'Bestätigungen')}</th>
                  <th className="px-4 py-3">{t('ocrLearning.colOrgs', 'Organisationen')}</th>
                  <th className="px-4 py-3">{t('ocrLearning.colStatus', 'Status')}</th>
                  <th className="px-4 py-3 text-right">{t('leads.colActions', 'Aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {terms.map((term) => (
                  <tr key={term.id} className="border-b border-line last:border-0 hover:bg-raised/50">
                    <td className="px-4 py-3 font-medium text-ink font-mono">{term.term}</td>
                    <td className="px-4 py-3 text-ink-muted">
                      {term.field === 'jobTitle'
                        ? t('leads.fieldJobTitle', 'Position')
                        : t('leads.fieldCompany', 'Firma')}
                    </td>
                    <td className="px-4 py-3 text-ink-muted">{term.totalCount}</td>
                    <td className="px-4 py-3 text-ink-muted">{term.orgCount} / {minOrgs}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        term.active
                          ? 'bg-success-500/10 text-success-600 dark:text-success-300 border-success-500/30'
                          : 'bg-raised text-ink-faint border-line'
                      }`}>
                        {term.active
                          ? t('ocrLearning.active', 'Aktiv')
                          : t('ocrLearning.pending', 'Sammelt')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setDeleting(term)}
                          title={t('leads.delete', 'Löschen')}
                          className="rounded-lg p-2 text-ink-muted hover:bg-danger-500/10 hover:text-danger-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        type="confirm"
        title={t('ocrLearning.deleteTitle', 'Begriff löschen?')}
        message={deleting ? t('ocrLearning.deleteMessage', '„{{term}}“ wird aus der Lernbasis entfernt und nicht mehr für die Erkennung verwendet.', { term: deleting.term }) : ''}
        confirmText={t('leads.delete', 'Löschen')}
        cancelText={t('common.cancel', 'Abbrechen')}
        onConfirm={() => { if (deleting) void removeTerm(deleting); }}
      />
    </Layout>
  );
}
